import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Tag as TagIcon, ChevronDown, Loader2, AlertCircle, Check, Search, Plus } from 'lucide-react';
import api from '../api';


export interface ClientTagOption {
  tag_id: number;
  tag_name: string;
  tag_type: 'client' | 'internal' | 'rnd';
  active: boolean;
  display_order: number;
}

interface ClientTagPickerProps {
  value: number | null;
  onChange: (tagId: number | null) => void;
  required?: boolean;
  autoSelectLastUsed?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  helpText?: string;
  errorMessage?: string;
  className?: string;
  // When true, shows a "+ Add new client" inline-create option at the
  // bottom of the dropdown. Used by the BD New Engagement modal so PMs
  // can spin up a new client tag without leaving the create flow.
  allowCreate?: boolean;
}

// Module-level cache so opening 5 different "new X" dialogs in a minute
// hits the API once, not five times.
let cachedTags: ClientTagOption[] | null = null;
let cachedLastUsed: number | null = null;
let cachedAt = 0;
const CACHE_TTL_MS = 60 * 1000;
type Listener = (tags: ClientTagOption[], lastUsed: number | null) => void;
const listeners: Set<Listener> = new Set();
let inflight: Promise<void> | null = null;

async function loadTags(force = false): Promise<{ tags: ClientTagOption[]; lastUsed: number | null }> {
  const now = Date.now();
  if (!force && cachedTags && (now - cachedAt) < CACHE_TTL_MS) {
    return { tags: cachedTags, lastUsed: cachedLastUsed };
  }
  if (inflight) {
    await inflight;
    return { tags: cachedTags || [], lastUsed: cachedLastUsed };
  }
  // Preserves whatever was thrown/constructed below — an axios error (has
  // .response.status/.data) for network/HTTP failures, or a plain Error for
  // a 200 response with success:false — so the catch site downstream can
  // log full diagnostic detail (status, response body) to the console.
  let loadError: unknown = null;
  inflight = (async () => {
    try {
      const res = await api.get('/api/client-tags', { timeout: 20000 });
      // A 200 response with success:false (or a malformed body) must not be
      // treated as "zero tags" — that renders as an indistinguishable-from-
      // empty "No active tags" with no error shown, hiding real failures
      // (auth/tenant mismatch, IP block, etc.) behind what looks like a
      // data problem. Surface it as an error instead.
      if (res.data && res.data.success) {
        cachedTags = Array.isArray(res.data.tags) ? res.data.tags : [];
        cachedLastUsed = typeof res.data.last_used_tag_id === 'number' ? res.data.last_used_tag_id : null;
        cachedAt = Date.now();
        listeners.forEach((l) => {
          try { l(cachedTags || [], cachedLastUsed); } catch (_) { /* noop */ }
        });
      } else {
        loadError = Object.assign(
          new Error(res.data?.error || 'Client tags request did not return success'),
          { responseData: res.data, status: res.status }
        );
      }
    } catch (err) {
      loadError = err;
    } finally {
      inflight = null;
    }
  })();
  await inflight;
  if (loadError) throw loadError;
  return { tags: cachedTags || [], lastUsed: cachedLastUsed };
}

export function invalidateClientTagCache() {
  cachedTags = null;
  cachedLastUsed = null;
  cachedAt = 0;
}

const TYPE_GROUP_LABEL: Record<ClientTagOption['tag_type'], string> = {
  client: 'Clients',
  internal: 'Internal',
  rnd: 'R&D / Learning',
};

const ClientTagPicker: React.FC<ClientTagPickerProps> = ({
  value,
  onChange,
  required = false,
  autoSelectLastUsed = true,
  disabled = false,
  placeholder = 'Pick a client engagement…',
  label,
  helpText,
  errorMessage,
  className = '',
  allowCreate = false,
}) => {
  const [tags, setTags] = useState<ClientTagOption[]>(cachedTags || []);
  const [lastUsed, setLastUsed] = useState<number | null>(cachedLastUsed);
  const [loading, setLoading] = useState<boolean>(!cachedTags);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Inline-create state. createMode flips the bottom of the dropdown from
  // the "+ Add new client" button to a tag-name input + Create button.
  const [createMode, setCreateMode] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(!cachedTags);
    loadTags()
      .then(({ tags: fresh, lastUsed: last }) => {
        if (cancelled) return;
        setTags(fresh);
        setLastUsed(last);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // Full diagnostic detail (HTTP status, response body, request config)
        // goes to the console for developers — the UI only ever shows a
        // generic message (see the "No active tags" block below), since a
        // raw backend error string isn't meaningful to an end user.
        console.error('[ClientTagPicker] Failed to load client tags:', {
          message: err?.message,
          status: err?.response?.status ?? err?.status,
          responseData: err?.response?.data ?? err?.responseData,
          url: err?.config?.url,
        });
        setError('load-failed');
        setLoading(false);
      });
    const listener: Listener = (fresh, last) => {
      setTags(fresh);
      setLastUsed(last);
    };
    listeners.add(listener);
    return () => {
      cancelled = true;
      listeners.delete(listener);
    };
  }, []);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    loadTags(true)
      .then(({ tags: fresh, lastUsed: last }) => {
        setTags(fresh);
        setLastUsed(last);
        setLoading(false);
      })
      .catch(() => {
        setError('load-failed');
        setLoading(false);
      });
  }, []);

  // Pre-select the user's last-used tag if no value is set yet.
  useEffect(() => {
    if (!autoSelectLastUsed) return;
    if (value !== null && value !== undefined) return;
    if (lastUsed && tags.some((t) => t.tag_id === lastUsed && t.active)) {
      onChange(lastUsed);
    }
  }, [autoSelectLastUsed, value, lastUsed, tags, onChange]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search when dropdown opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [open]);

  const selectedTag = useMemo(
    () => (value !== null && value !== undefined ? tags.find((t) => t.tag_id === value) : null),
    [tags, value]
  );

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filterFn = (t: ClientTagOption) =>
      t.active && (!term || t.tag_name.toLowerCase().includes(term));
    const clients = tags
      .filter((t) => t.tag_type === 'client')
      .filter(filterFn)
      .sort((a, b) => a.tag_name.localeCompare(b.tag_name));
    const internal = tags
      .filter((t) => t.tag_type === 'internal')
      .filter(filterFn)
      .sort((a, b) => a.display_order - b.display_order || a.tag_name.localeCompare(b.tag_name));
    const rnd = tags
      .filter((t) => t.tag_type === 'rnd')
      .filter(filterFn)
      .sort((a, b) => a.display_order - b.display_order || a.tag_name.localeCompare(b.tag_name));
    return { clients, internal, rnd };
  }, [tags, search]);

  const totalVisible = grouped.clients.length + grouped.internal.length + grouped.rnd.length;
  const showingValidation = required && (value === null || value === undefined) && !loading;
  const visibleError = errorMessage || (showingValidation ? 'Required' : null);

  const handleSelect = useCallback(
    (tagId: number) => {
      onChange(tagId);
      setOpen(false);
      setSearch('');
      setCreateMode(false);
      setCreateName('');
      setCreateError(null);
    },
    [onChange]
  );

  // Reset inline-create state whenever the dropdown closes so reopening
  // always starts at the clean "+ Add new client" prompt.
  useEffect(() => {
    if (!open) {
      setCreateMode(false);
      setCreateName('');
      setCreateError(null);
    }
  }, [open]);

  // Focus the create input the moment the user enters create mode.
  useEffect(() => {
    if (createMode && createInputRef.current) {
      setTimeout(() => createInputRef.current?.focus(), 50);
    }
  }, [createMode]);

  const handleCreate = useCallback(async () => {
    const name = createName.trim();
    if (!name) {
      setCreateError('Client name required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await api.post('/api/client-tags', { tag_name: name, tag_type: 'client' });
      const created = res?.data?.tag as ClientTagOption | undefined;
      if (!created || !created.tag_id) {
        throw new Error('Server did not return the created tag');
      }
      // Force-refresh cache so other open ClientTagPickers see the new tag too.
      invalidateClientTagCache();
      const { tags: fresh, lastUsed: last } = await loadTags(true);
      setTags(fresh);
      setLastUsed(last);
      // Select the newly-created tag immediately.
      handleSelect(created.tag_id);
    } catch (err: any) {
      setCreateError(
        err?.response?.data?.error || err?.message || 'Failed to create client tag'
      );
    } finally {
      setCreating(false);
    }
  }, [createName, handleSelect]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || loading) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((o) => !o);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <div className={className} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <TagIcon className="inline w-3.5 h-3.5 mr-1 text-gray-500" />
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && !loading && setOpen((o) => !o)}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`w-full pl-3 pr-9 py-2 text-sm text-left border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors ${
            visibleError ? 'border-red-300' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {loading ? (
            <span className="text-gray-400">Loading client tags…</span>
          ) : selectedTag ? (
            <span className="flex items-center gap-2 text-gray-900">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                selectedTag.tag_type === 'client' ? 'bg-blue-500' :
                selectedTag.tag_type === 'internal' ? 'bg-purple-500' : 'bg-amber-500'
              }`} />
              <span className="font-medium">{selectedTag.tag_name}</span>
              <span className="text-xs text-gray-500">
                {selectedTag.tag_type === 'client' ? 'Client' : selectedTag.tag_type === 'internal' ? 'Internal' : 'R&D / Learning'}
              </span>
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </button>

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          {loading ? (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </div>

        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tags…"
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setOpen(false);
                      setSearch('');
                    }
                  }}
                />
              </div>
            </div>

            {/* Option list */}
            <div className="max-h-64 overflow-y-auto py-1">
              {totalVisible === 0 && (
                <div className={`px-3 py-4 text-sm text-center ${error ? 'text-red-600' : 'text-gray-500'}`}>
                  {error ? (
                    <div className="flex flex-col items-center gap-2">
                      <span>Unable to load client list.</span>
                      <button
                        type="button"
                        onClick={retry}
                        className="px-3 py-1 text-xs font-medium text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50"
                      >
                        Retry
                      </button>
                    </div>
                  ) : search ? `No tags match "${search}"` : 'No active tags'}
                </div>
              )}

              {(['clients', 'internal', 'rnd'] as const).map((bucket) => {
                const items = grouped[bucket];
                if (items.length === 0) return null;
                const label = bucket === 'clients' ? 'Clients' : bucket === 'internal' ? 'Internal' : 'R&D / Learning';
                const dotColor = bucket === 'clients' ? 'bg-blue-500' : bucket === 'internal' ? 'bg-purple-500' : 'bg-amber-500';
                return (
                  <div key={bucket}>
                    <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
                    {items.map((t) => {
                      const isSelected = value === t.tag_id;
                      return (
                        <button
                          key={t.tag_id}
                          type="button"
                          onClick={() => handleSelect(t.tag_id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                            isSelected ? 'bg-blue-50 text-blue-900 font-medium' : 'text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor}`} />
                          <span className="flex-1 truncate">{t.tag_name}</span>
                          {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {allowCreate && (
              <div className="border-t border-gray-100 bg-gray-50">
                {!createMode ? (
                  <button
                    type="button"
                    onClick={() => {
                      setCreateMode(true);
                      if (search.trim() && !tags.some((t) => t.tag_name.toLowerCase() === search.trim().toLowerCase())) {
                        setCreateName(search.trim());
                      }
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-blue-700 hover:bg-blue-50 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="font-medium">Add new client</span>
                    {search.trim() && !tags.some((t) => t.tag_name.toLowerCase() === search.trim().toLowerCase()) && (
                      <span className="text-xs text-gray-500 truncate">— "{search.trim()}"</span>
                    )}
                  </button>
                ) : (
                  <div className="p-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        ref={createInputRef}
                        type="text"
                        value={createName}
                        onChange={(e) => {
                          setCreateName(e.target.value);
                          if (createError) setCreateError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreate();
                          } else if (e.key === 'Escape') {
                            setCreateMode(false);
                            setCreateName('');
                            setCreateError(null);
                          }
                        }}
                        placeholder="New client name…"
                        disabled={creating}
                        maxLength={128}
                        className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      />
                      <button
                        type="button"
                        onClick={handleCreate}
                        disabled={creating || !createName.trim()}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md transition-colors flex items-center gap-1.5"
                      >
                        {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        Create
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCreateMode(false);
                          setCreateName('');
                          setCreateError(null);
                        }}
                        disabled={creating}
                        className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    {createError && (
                      <div className="flex items-center gap-1 text-xs text-red-600">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>{createError}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {visibleError && (
        <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>{visibleError}</span>
        </div>
      )}
      {!visibleError && (helpText || error) && (
        error ? (
          <p className="mt-1 text-xs text-red-600">
            Unable to load client list.{' '}
            <button type="button" onClick={retry} className="underline font-medium hover:text-red-700">
              Retry
            </button>
          </p>
        ) : (
          <p className="mt-1 text-xs text-gray-500">{helpText}</p>
        )
      )}
    </div>
  );
};

export default ClientTagPicker;
